import { type CalendarDate, type Time, today } from '@internationalized/date';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, I18nProvider } from 'react-aria-components';
import CalendarContent from '../shared/CalendarContent/CalendarContent';
import HourField from '../shared/HourField/HourField';
import { jsDateToCalendarDate, TIME_ZONE, toJsDate } from '../shared/dateUtils';
import { usePopoverDateValue } from '../shared/usePopoverDateValue';

/**
 * DatePickerInline — inline date (and optional time) picker built on react-aria. Meant to be rendered inside a
 * `Dialog`: react-aria's `Calendar` brings full WAI-ARIA keyboard navigation for the day grid (arrows,
 * PageUp/Down, Home/End) and the segmented `TimeField` is keyboard-accessible too. Colors are themed entirely
 * with `--ui-*` tokens.
 *
 * The public contract uses plain JS `Date`: it accepts `value: Date | string | null` and emits a `Date | null`
 * via `onChange`. All `@internationalized/date` conversion lives in `../shared/dateUtils`.
 */

export type DatePickerInlineProps = {
  // Extra content rendered in the same centered row as the TimeField (e.g. a deliver-late checkbox).
  children?: React.ReactNode;
  // Shows a native year <select> in the calendar header (month stays on prev/next).
  hasYearPicker?: boolean;
  minDate?: Date | string | null;
  onChange?: (date: Date | null) => void;
  // Fires when the visible month/year changes (prev/next, PageUp/Down, year picker) — independent of `onChange`,
  // which only fires on day selection. Useful for consumers that need to refetch data for the visible month.
  onMonthChange?: (date: Date) => void;
  shouldDisablePast?: boolean;
  showHour?: boolean;
  value?: Date | string | null;
  // Jumps the visible month from outside (`value` alone does not: react-aria seeds the visible month from it only
  // on mount). Pass a *stable* reference — a new Date on every render re-triggers the jump and fights the user's
  // own navigation. Only the month/year is read; the day is ignored. Leave it out to keep navigation fully internal.
  visibleMonth?: Date | string | null;
};

function DatePickerInline({
  children,
  hasYearPicker = false,
  minDate = null,
  onChange = () => {},
  onMonthChange = () => {},
  shouldDisablePast = true,
  showHour = false,
  value = null,
  visibleMonth,
}: DatePickerInlineProps): JSX.Element {
  const { t, i18n } = useTranslation();

  // Shared date+time working value (CalendarDate + Time split + dateUtils glue), same as DatePicker/DatePickerActions.
  const dateState = usePopoverDateValue(toJsDate(value));

  // Re-sync when the consumer updates `value` from outside.
  const [prevValue, setPrevValue] = React.useState<Date | string | null | undefined>(value);
  if (value !== prevValue) {
    setPrevValue(value);
    dateState.setFromJsDate(toJsDate(value));
  }

  // An explicit minDate wins; otherwise fall back to "no past dates" when shouldDisablePast is on.
  const minValue = jsDateToCalendarDate(toJsDate(minDate)) ?? (shouldDisablePast ? today(TIME_ZONE) : undefined);

  // Tracks the focused day so `onMonthChange` only fires when month/year actually change — react-aria's
  // `onFocusChange` fires on every focus move (arrow keys, day selection), not just month navigation.
  const [focusedDate, setFocusedDate] = React.useState<CalendarDate>(dateState.dateValue ?? minValue ?? today(TIME_ZONE));

  // Edge-triggered on the `visibleMonth` reference so the jump happens once per external change and internal
  // navigation stays free afterwards (a level comparison against `focusedDate` would drag the user back on
  // every render when the consumer does not track the month itself).
  const [prevVisibleMonth, setPrevVisibleMonth] = React.useState<Date | string | null | undefined>(visibleMonth);
  if (visibleMonth !== prevVisibleMonth) {
    setPrevVisibleMonth(visibleMonth);
    const nextVisible = jsDateToCalendarDate(toJsDate(visibleMonth));
    if (nextVisible && (nextVisible.month !== focusedDate.month || nextVisible.year !== focusedDate.year)) {
      setFocusedDate(nextVisible);
    }
  }

  function handleDateChange(nextDate: CalendarDate): void {
    onChange(dateState.changeDate(nextDate));
  }

  function handleTimeChange(nextTime: Time | null): void {
    onChange(dateState.changeTime(nextTime));
  }

  function handleFocusChange(nextFocusedDate: CalendarDate): void {
    if (nextFocusedDate.month !== focusedDate.month || nextFocusedDate.year !== focusedDate.year) {
      onMonthChange(nextFocusedDate.toDate(TIME_ZONE));
    }
    setFocusedDate(nextFocusedDate);
  }

  return (
    <I18nProvider locale={i18n.language}>
      <div className="date-picker-inline">
        <Calendar
          aria-label={t('common:Select date')}
          className="month-calendar date-picker-inline__calendar"
          // Controlled focus only when the consumer drives the visible month; `handleFocusChange` mirrors every
          // move back into state, so arrow keys and prev/next behave as they do uncontrolled. Consumers that omit
          // `visibleMonth` keep react-aria's own uncontrolled focus untouched.
          focusedValue={visibleMonth === undefined ? undefined : focusedDate}
          minValue={minValue}
          onChange={handleDateChange}
          onFocusChange={handleFocusChange}
          value={dateState.dateValue}
        >
          <CalendarContent hasYearPicker={hasYearPicker} locale={i18n.language} />
        </Calendar>

        {(showHour || children) && (
          <div className="date-picker-inline__bottom">
            {showHour && <HourField onChange={handleTimeChange} value={dateState.timeValue} />}
            {children}
          </div>
        )}
      </div>
    </I18nProvider>
  );
}

export default DatePickerInline;
